import { useState } from "react";
import { Menu } from "lucide-react";
import type { SuperAdminPlatformAdmin } from "@multi-chat/shared";
import { useTranslation } from "react-i18next";
import { isApiError } from "../../api/client.js";
import { Badge } from "../../components/ui/badge/index.js";
import { Button } from "../../components/ui/button/index.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import { useOptionalNotifications } from "../notifications/notification-context.js";
import type { SuperAdminLoadState } from "./SuperAdminDashboardPage.js";
import { superAdminInputClassName } from "./super-admin-setting-rows.js";

const ADD_ERROR_KEYS: Readonly<Record<string, string>> = {
  PLATFORM_ADMIN_USER_NOT_FOUND: "superAdmin.team.errors.userNotFound",
  PLATFORM_ADMIN_ALREADY_ACTIVE: "superAdmin.team.errors.alreadyActive",
  VALIDATION_FAILED: "superAdmin.team.errors.invalidEmail",
};

const REMOVE_ERROR_KEYS: Readonly<Record<string, string>> = {
  PLATFORM_ADMIN_PROTECTED: "superAdmin.team.errors.protected",
  PLATFORM_ADMIN_LAST: "superAdmin.team.errors.last",
};

function errorKey(error: unknown, keys: Readonly<Record<string, string>>, fallback: string): string {
  return isApiError(error) && error.code ? (keys[error.code] ?? fallback) : fallback;
}

export function SuperAdminTeamSection({
  state,
  currentUserId,
  onRetry,
  onAdd,
  onRemove,
  onOpenMobileNav,
}: {
  state: SuperAdminLoadState<SuperAdminPlatformAdmin[]>;
  currentUserId: number;
  onRetry: () => void;
  onAdd: (email: string) => Promise<void>;
  onRemove: (admin: SuperAdminPlatformAdmin) => Promise<void>;
  onOpenMobileNav: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="super-admin-settings-team">
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-line/70 bg-surface px-4 py-3.5">
        <div className="min-w-0">
          <h1 className="m-0 text-[1rem] font-semibold tracking-[-0.01em] text-foreground">
            {t("superAdmin.team.title")}
          </h1>
          <p className="m-0 mt-1 max-w-2xl text-[0.75rem] leading-5 text-muted">{t("superAdmin.team.hint")}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 lg:hidden"
          onClick={onOpenMobileNav}
          data-testid="super-admin-settings-open-nav"
        >
          <Menu size={14} aria-hidden="true" />
          {t("superAdmin.settings.nav.mobileMenu")}
        </Button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-8">
        <div className="grid w-full gap-4">
          {state.status === "loading" ? (
            <div className="h-40 animate-pulse rounded-xl border border-line bg-surface" data-testid="super-admin-team-loading" aria-busy="true" />
          ) : null}
          {state.status === "error" ? (
            <ProductErrorState
              testId="super-admin-team-error"
              title={t("superAdmin.team.loadErrorTitle")}
              description={t("superAdmin.team.loadErrorBody")}
              onRetry={onRetry}
              retryLabel={t("superAdmin.retry")}
            />
          ) : null}
          {state.status === "ready" ? (
            <>
              <AddPlatformAdminForm onAdd={onAdd} />
              <PlatformAdminList admins={state.data} currentUserId={currentUserId} onRemove={onRemove} />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function AddPlatformAdminForm({ onAdd }: { onAdd: (email: string) => Promise<void> }): React.JSX.Element {
  const { t } = useTranslation();
  const notifications = useOptionalNotifications();
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    const value = email.trim();
    if (!value) return;
    setPending(true);
    setError(null);
    try {
      await onAdd(value);
      setEmail("");
      notifications?.pushNotification({ message: t("superAdmin.team.added") });
    } catch (caught) {
      setError(errorKey(caught, ADD_ERROR_KEYS, "superAdmin.team.errors.addFailed"));
    } finally {
      setPending(false);
    }
  }

  return (
    <form
      className="grid gap-2 rounded-xl border border-line bg-surface px-4 py-4"
      data-testid="super-admin-team-add"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <label className="text-sm font-medium text-foreground" htmlFor="super-admin-team-email">
        {t("superAdmin.team.addLabel")}
      </label>
      <p className="m-0 text-[0.75rem] leading-5 text-muted">{t("superAdmin.team.addHint")}</p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          id="super-admin-team-email"
          type="email"
          dir="ltr"
          autoComplete="off"
          aria-invalid={error !== null}
          className={`${superAdminInputClassName} max-w-sm flex-1`}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setError(null);
          }}
          data-testid="super-admin-team-email"
        />
        <Button type="submit" size="sm" disabled={pending || email.trim() === ""} data-testid="super-admin-team-add-submit">
          {pending ? t("superAdmin.team.adding") : t("superAdmin.team.add")}
        </Button>
      </div>
      {error ? (
        <p className="m-0 text-sm text-destructive" role="alert" data-testid="super-admin-team-add-error">
          {t(error)}
        </p>
      ) : null}
    </form>
  );
}

function PlatformAdminList({
  admins,
  currentUserId,
  onRemove,
}: {
  admins: SuperAdminPlatformAdmin[];
  currentUserId: number;
  onRemove: (admin: SuperAdminPlatformAdmin) => Promise<void>;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const notifications = useOptionalNotifications();
  const [target, setTarget] = useState<SuperAdminPlatformAdmin | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastAdmin = admins.length <= 1;
  const removingSelf = target?.userId === currentUserId;

  async function confirm(): Promise<void> {
    if (!target) return;
    setPending(true);
    setError(null);
    try {
      await onRemove(target);
      setTarget(null);
      notifications?.pushNotification({ message: t("superAdmin.team.removed") });
    } catch (caught) {
      setError(errorKey(caught, REMOVE_ERROR_KEYS, "superAdmin.team.errors.removeFailed"));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="grid gap-3">
      <div>
        <h2 className="m-0 text-sm font-semibold text-foreground">{t("superAdmin.team.listTitle")}</h2>
        {lastAdmin ? (
          <p className="m-0 mt-1 text-sm text-muted" data-testid="super-admin-team-last-hint">
            {t("superAdmin.team.lastHint")}
          </p>
        ) : null}
      </div>
      <ul
        className="m-0 list-none divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface p-0"
        data-testid="super-admin-team-list"
      >
        {admins.map((admin) => (
          <li
            key={admin.id}
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
            data-testid={`super-admin-team-row-${admin.id}`}
          >
            <div className="grid min-w-0 gap-0.5">
              <span className="flex flex-wrap items-center gap-2 text-[0.8125rem] font-medium text-foreground">
                {admin.displayName}
                {admin.isProtected ? (
                  <Badge variant="outline" data-testid={`super-admin-team-protected-${admin.id}`}>
                    {t("superAdmin.team.protected")}
                  </Badge>
                ) : null}
                {admin.userId === currentUserId ? <Badge variant="default">{t("superAdmin.team.you")}</Badge> : null}
              </span>
              <span className="text-[0.75rem] text-muted">
                <LtrValue>{admin.email}</LtrValue>
                <span className="px-2">·</span>
                {t("superAdmin.team.since")}{" "}
                <LtrValue>{new Date(admin.createdAt).toLocaleDateString(i18n.language)}</LtrValue>
              </span>
            </div>
            {admin.isProtected ? (
              <span className="text-[0.75rem] text-muted">{t("superAdmin.team.protectedHint")}</span>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 border-destructive/35 bg-transparent text-destructive hover:bg-error-muted"
                disabled={lastAdmin}
                data-testid={`super-admin-team-remove-${admin.id}`}
                onClick={() => {
                  setError(null);
                  setTarget(admin);
                }}
              >
                {t("superAdmin.team.remove")}
              </Button>
            )}
          </li>
        ))}
      </ul>
      <AlertDialog
        open={target !== null}
        onOpenChange={(open) => {
          if (pending) return;
          if (!open) {
            setTarget(null);
            setError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>{t("superAdmin.team.removeDialog.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {removingSelf
              ? t("superAdmin.team.removeDialog.selfBody")
              : t("superAdmin.team.removeDialog.body", { name: target?.displayName ?? "" })}
          </AlertDialogDescription>
          {error ? (
            <p className="m-0 text-sm text-destructive" role="alert">
              {t(error)}
            </p>
          ) : null}
          <AlertDialogActions>
            <Button type="button" variant="outline" size="sm" disabled={pending} onClick={() => setTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={pending}
              data-testid="super-admin-team-remove-confirm"
              onClick={() => void confirm()}
            >
              {t("superAdmin.team.removeDialog.confirm")}
            </Button>
          </AlertDialogActions>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
